import React, { useState } from 'react';
import { aiAPI } from '../../utils/api';
import { X, Loader2, Search, Plus, Check, AlertCircle, ShieldCheck, ShieldAlert } from 'lucide-react';

var KEY_POSITIONS = [
  { id: 'ceo', label: 'CEO / Managing Director' },
  { id: 'coo', label: 'COO / Operations Director' },
  { id: 'cfo', label: 'CFO / Finance Director' },
  { id: 'chro', label: 'CHRO / Head of HR' },
  { id: 'cto', label: 'CTO / Head of Technology' },
  { id: 'head_ld', label: 'Head of L&D / Training' },
  { id: 'head_transformation', label: 'Head of Transformation' },
  { id: 'head_strategy', label: 'Head of Strategy / Planning' },
  { id: 'head_talent', label: 'Head of Talent Management' },
  { id: 'head_od', label: 'Head of OD / Change Mgmt' },
  { id: 'board_chairman', label: 'Board Chairman' },
  { id: 'general_manager', label: 'General Manager' },
];

function AIFindContactsModal({ isOpen, onClose, client, onContactsSaved }) {
  var s1 = useState(false); var loading = s1[0]; var setLoading = s1[1];
  var s2 = useState([]); var results = s2[0]; var setResults = s2[1];
  var s3 = useState({}); var selected = s3[0]; var setSelected = s3[1];
  var s4 = useState(false); var saving = s4[0]; var setSaving = s4[1];
  var s5 = useState(null); var error = s5[0]; var setError = s5[1];
  var s6 = useState(false); var saved = s6[0]; var setSaved = s6[1];
  var s7 = useState({}); var selectedRoles = s7[0]; var setSelectedRoles = s7[1];
  var s8 = useState(''); var customRoles = s8[0]; var setCustomRoles = s8[1];

  if (!isOpen) return null;

  var toggleRole = function(roleId) {
    var newRoles = {};
    var keys = Object.keys(selectedRoles);
    for (var k = 0; k < keys.length; k++) newRoles[keys[k]] = true;
    if (newRoles[roleId]) { delete newRoles[roleId]; } else { newRoles[roleId] = true; }
    setSelectedRoles(newRoles);
  };

  var selectAllRoles = function() {
    var newRoles = {};
    for (var i = 0; i < KEY_POSITIONS.length; i++) newRoles[KEY_POSITIONS[i].id] = true;
    setSelectedRoles(newRoles);
  };

  var clearAllRoles = function() {
    setSelectedRoles({});
  };

  var handleSearch = async function() {
    setLoading(true);
    setError(null);
    setResults([]);
    setSaved(false);
    try {
      var rolesList = [];
      for (var i = 0; i < KEY_POSITIONS.length; i++) {
        if (selectedRoles[KEY_POSITIONS[i].id]) rolesList.push(KEY_POSITIONS[i].label);
      }
      if (customRoles.trim()) {
        var custom = customRoles.split(',');
        for (var c = 0; c < custom.length; c++) {
          if (custom[c].trim()) rolesList.push(custom[c].trim());
        }
      }
      var res = await aiAPI.findContacts({
        organization_name: client.client_name,
        website: client.website || '',
        sector: client.sector || client.industry || '',
        roles: rolesList,
      });
      var contacts = res.data.contacts || [];
      setResults(contacts);
      var sel = {};
      for (var j = 0; j < contacts.length; j++) {
        if (contacts[j].verified || contacts[j].confidence >= 60) sel[j] = true;
      }
      setSelected(sel);
    } catch (err) {
      var msg = 'AI search failed. Please try again.';
      if (err.response && err.response.data) msg = err.response.data.detail || msg;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  var toggleSelect = function(index) {
    var newSel = {};
    var keys = Object.keys(selected);
    for (var k = 0; k < keys.length; k++) newSel[keys[k]] = true;
    if (newSel[index]) { delete newSel[index]; }
    else { newSel[index] = true; }
    setSelected(newSel);
  };

  var handleSave = async function() {
    var selContacts = [];
    for (var i = 0; i < results.length; i++) {
      if (selected[i]) selContacts.push(results[i]);
    }
    if (selContacts.length === 0) return;
    setSaving(true);
    try {
      await aiAPI.saveContacts({ client_id: client._id, contacts: selContacts });
      setSaved(true);
      if (onContactsSaved) onContactsSaved();
    } catch (err) {
      setError('Failed to save contacts');
    } finally {
      setSaving(false);
    }
  };

  var selectedCount = Object.keys(selected).length;
  var selectedRoleCount = Object.keys(selectedRoles).length;

  var roleCheckboxes = [];
  for (var ri = 0; ri < KEY_POSITIONS.length; ri++) {
    var pos = KEY_POSITIONS[ri];
    var isChecked = !!selectedRoles[pos.id];
    roleCheckboxes.push(
      <RoleCheckbox key={pos.id} id={pos.id} label={pos.label} checked={isChecked} onToggle={toggleRole} />
    );
  }

  var resultItems = [];
  for (var ci = 0; ci < results.length; ci++) {
    resultItems.push(
      <ContactResultItem key={ci} idx={ci} contact={results[ci]} isSel={!!selected[ci]} onToggle={toggleSelect} />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-find-contacts-modal">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={function(e) { e.stopPropagation(); }}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">AI Contact Finder</h2>
            <p className="text-sm text-slate-500">{client.client_name} {client.website ? '(' + client.website + ')' : ''}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
        </div>

        <div className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}

          {results.length === 0 && !loading && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Claude AI will search for key contacts at <strong>{client.client_name}</strong> by scraping their website and validating against public information.</p>

              {client.website && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
                  <ShieldCheck size={16} /> Website available: contacts will be validated against {client.website}
                </div>
              )}
              {!client.website && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
                  <ShieldAlert size={16} /> No website on record. Results will be marked as unverified. Add a website for better accuracy.
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">Select Key Positions to Find</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={selectAllRoles} className="text-xs text-primary hover:underline" data-testid="select-all-roles">Select All</button>
                    <button type="button" onClick={clearAllRoles} className="text-xs text-slate-500 hover:underline" data-testid="clear-all-roles">Clear</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2" data-testid="role-checkboxes">
                  {roleCheckboxes}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Additional Roles (optional)</label>
                <input type="text" value={customRoles} onChange={function(e) { setCustomRoles(e.target.value); }} placeholder="e.g. VP Marketing, Head of Digital" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ai-custom-roles-input" />
                <p className="text-xs text-slate-400 mt-1">Comma-separated custom roles</p>
              </div>

              <button onClick={handleSearch} disabled={loading || (selectedRoleCount === 0 && !customRoles.trim())} className="w-full py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ai-search-contacts-btn">
                <Search size={18} /> Find Contacts with AI
              </button>
            </div>
          )}

          {loading && (
            <div className="text-center py-12">
              <Loader2 size={32} className="animate-spin text-primary mx-auto mb-3" />
              <p className="text-sm text-slate-600">Scanning website & validating contacts...</p>
              <p className="text-xs text-slate-400 mt-1">This may take 15-30 seconds</p>
            </div>
          )}

          {results.length > 0 && !loading && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700">Found {results.length} potential contacts</p>
                <p className="text-sm text-slate-500">{selectedCount} selected</p>
              </div>

              <VerificationLegend results={results} />

              {resultItems}
              <div className="flex gap-3 pt-2">
                <button onClick={function() { setResults([]); setSelected({}); setSaved(false); }} className="flex-1 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50" data-testid="ai-search-again-btn">Search Again</button>
                {saved ? (
                  <div className="flex-1 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 text-center flex items-center justify-center gap-2"><Check size={16} /> Contacts Saved!</div>
                ) : (
                  <button onClick={handleSave} disabled={saving || selectedCount === 0} className="flex-1 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="ai-save-contacts-btn">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                    Add {selectedCount} to CRM
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RoleCheckbox({ id, label, checked, onToggle }) {
  return (
    <label className={'flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ' + (checked ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 text-slate-600 hover:bg-slate-50')} data-testid={'role-checkbox-' + id}>
      <div className={'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ' + (checked ? 'bg-primary border-primary' : 'border-slate-300')}>
        {checked && <Check size={12} className="text-white" />}
      </div>
      <input type="checkbox" checked={checked} onChange={function() { onToggle(id); }} className="sr-only" />
      <span>{label}</span>
    </label>
  );
}

function VerificationLegend({ results }) {
  var verified = 0;
  var unverified = 0;
  for (var i = 0; i < results.length; i++) {
    if (results[i].verified) verified++;
    else unverified++;
  }
  return (
    <div className="flex gap-4 p-3 bg-slate-50 rounded-lg text-xs">
      <span className="flex items-center gap-1.5 text-green-700"><ShieldCheck size={14} /> {verified} verified from website</span>
      <span className="flex items-center gap-1.5 text-amber-600"><ShieldAlert size={14} /> {unverified} unverified (AI inference)</span>
    </div>
  );
}

function ContactResultItem({ idx, contact, isSel, onToggle }) {
  var confColor = contact.confidence >= 70 ? 'text-green-600' : contact.confidence >= 40 ? 'text-amber-600' : 'text-red-500';
  var boxCls = isSel ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300';
  var checkCls = isSel ? 'bg-primary border-primary' : 'border-slate-300';
  var dept = contact.department ? ' \u00B7 ' + contact.department : '';
  var isVerified = contact.verified;
  return (
    <div onClick={function() { onToggle(idx); }} className={'p-4 rounded-lg border cursor-pointer transition-all ' + boxCls} data-testid={'ai-contact-result-' + idx}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={'w-5 h-5 mt-0.5 rounded border flex items-center justify-center flex-shrink-0 ' + checkCls}>
            {isSel && <Check size={14} className="text-white" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-slate-900">{contact.name}</h4>
              {isVerified ? (
                <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded"><ShieldCheck size={12} /> Verified</span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded"><ShieldAlert size={12} /> Unverified</span>
              )}
            </div>
            <p className="text-sm text-slate-600">{contact.title}{dept}</p>
            {contact.email && <p className="text-xs text-slate-500 mt-1">{contact.email}</p>}
            {contact.reasoning && <p className="text-xs text-slate-400 mt-1 italic">{contact.reasoning}</p>}
          </div>
        </div>
        <span className={'text-xs font-medium ' + confColor}>{contact.confidence}%</span>
      </div>
    </div>
  );
}

export default AIFindContactsModal;
